import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Box, Grid, Paper, Button, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import Layout from 'components/Layout';
import { Formik, Field } from 'formik';
import { useParams } from 'react-router-dom';
import useStyles from './BusinessHours.styled';
import { TextField } from 'components/FormFields';
import { useDispatch, useSelector } from 'react-redux';
import { parseBusinessHoursData } from 'services/rules';
import { BusinessHoursTable } from 'components/BusinessHours';
import { BusinessHoursActions } from 'state/modules/businessHours';

const BusinessHoursCreate = ({ history }) => {
  const classes = useStyles();

  const dispatch = useDispatch();
  const formRef = useRef(null);
  const { id, action } = useParams();
  const {
    loading,
    currentId,
    current: { name, bh_rules, description, error_message },
    fetchedCreateBH,
    fetchedOverwriteBH,
  } = useSelector((state) => state.businessHours);

  const initialValues = {
    bh_rules: bh_rules || [],
    description: description || '',
    error_message: error_message || '',
    name: action === 'clone' ? i18n.t('common.copy_of', { name }) : name || '',
  };

  const formSchema = Yup.object().shape({
    error_message: Yup.string().required(i18n.t('common.required_field')),
    description: Yup.string().required(i18n.t('common.required_field')),
    name: Yup.string().required(i18n.t('common.required_field')),
    bh_rules: Yup.array(),
  });

  const onSubmitHours = (bh) => {
    dispatch(
      BusinessHoursActions.updateCurrentBH({
        bh_rules: bh,
        name: formRef.current.values.name,
        description: formRef.current.values.description,
        error_message: formRef.current.values.error_message,
      })
    );

    formRef.current.setFieldValue('bh_rules', bh);
  };

  const onSubmit = (values) => {
    if (action === 'update' && id) {
      dispatch(BusinessHoursActions.fetchOverwriteBH(id, values));
    } else {
      dispatch(BusinessHoursActions.fetchCreateBH(values));
    }
  };

  const onDelete = (row) => dispatch(BusinessHoursActions.handleDeleteBusinessHours(row));

  useEffect(() => {
    if ((fetchedCreateBH || fetchedOverwriteBH) && currentId) {
      history.push(buildUrl(`/business-hours/${currentId}`));
      dispatch(BusinessHoursActions.resetCurrentBusinessHour());
    }
  }, [dispatch, history, fetchedCreateBH, fetchedOverwriteBH, currentId]);

  useEffect(() => {
    if (id) {
      dispatch(BusinessHoursActions.fetchBusinessHourById(id));
    }

    return () => {
      dispatch(BusinessHoursActions.resetCurrentBusinessHour());
    };
  }, [id, dispatch]);

  return (
    <Layout title={i18n.t('business_hours.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Title title={i18n.t(`business_hours.${action || 'create'}`)} />
              </Grid>
              <Grid item>
                <Grid container spacing={2} direction="row" alignItems="center">
                  <Grid item>
                    <Button
                      data-cy="btn-cancel"
                      color="primary"
                      disabled={loading}
                      variant="outlined"
                      style={{ textTransform: 'capitalize' }}
                      onClick={() => history.push(buildUrl(`/business-hours`))}
                    >
                      {i18n.t('common.cancel')}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      data-cy="btn-save"
                      color="primary"
                      disabled={loading}
                      variant="contained"
                      style={{ textTransform: 'capitalize' }}
                      onClick={() => formRef.current.submitForm()}
                    >
                      {i18n.t('common.save')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={2} className={classes.paper}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography align="left">
                      <Box mt={1} fontSize={14} component="span" fontWeight="medium">
                        {i18n.t('business_hours.subtitle')}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Formik
                      initialValues={initialValues}
                      validationSchema={formSchema}
                      enableReinitialize
                      innerRef={formRef}
                      onSubmit={onSubmit}
                    >
                      {({ handleSubmit }) => (
                        <form noValidate onSubmit={handleSubmit}>
                          <Grid container spacing={2} direction="row">
                            <Grid item xs={12} sm={4}>
                              <Field
                                name="name"
                                margin="none"
                                component={TextField}
                                inputProps={{ autoComplete: 'nope' }}
                                label={i18n.t('business_hours.ruleset_name')}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                margin="none"
                                name="description"
                                component={TextField}
                                label={i18n.t('common.description')}
                                inputProps={{ autoComplete: 'nope' }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography align="left">
                                <Box mt={1} fontSize={14} component="span" fontWeight="medium">
                                  {i18n.t('business_hours.error_msg_subtitle_1')}
                                </Box>
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography align="left">
                                <Box mt={1} fontSize={14} component="span" fontWeight="medium">
                                  {i18n.t('business_hours.error_msg_subtitle_2')}
                                </Box>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={10}>
                              <Field
                                required
                                margin="none"
                                name="error_message"
                                component={TextField}
                                inputProps={{ autoComplete: 'nope' }}
                                label={i18n.t('business_hours.error_msg_placeholder')}
                              />
                            </Grid>
                          </Grid>
                        </form>
                      )}
                    </Formik>
                  </Grid>
                  <Grid item xs={12} style={{ marginTop: 25 }}>
                    <BusinessHoursTable
                      id={id}
                      action={action}
                      rules={bh_rules}
                      onDelete={onDelete}
                      onSubmitHours={onSubmitHours}
                      bh={parseBusinessHoursData(bh_rules)}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

BusinessHoursCreate.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default withRouter(BusinessHoursCreate);
